export interface FpBulkLookupOption {
  id: string | number;
  name: string;
}

export interface FpBulkHealthColorOption {
  id: string;
  name: string;
  label?: string;
  colorCode?: string;
}

export interface FpBulkEntitySelections {
  countryId?: string | null;
  stateId?: string | null;
  cityId?: string | null;
  pincodeId?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  cityName?: string | null;
  pincode?: string | null;
  licenseImage?: string | null;
}

export interface FpBulkSkuDraft {
  name: string;
  netWeight: string;
  mrp: string;
  manufacturedBy: string;
  marketedBy: string;
  packagedBy: string;
  unitId?: string | null;
  unitName?: string | null;
  countryId?: string | null;
  countryName?: string | null;
  productImage?: string | null;
  productBarcode?: string | null;
}

export interface FpBulkIngredientDraft {
  name: string;
  amount: string;
  unit: string;
}

export interface FpBulkResolvedIngredient {
  /** IngredientMasters.Id — used for GetById / serving units. */
  ingredientId: string;
  /** IngredientRefs.Id — required for FinishProductIngredients FK. */
  ingredientRefId?: string;
  name: string;
  servingUnitMasterId: string;
  unit: string;
  amount: string;
  insNumber?: string;
  category?: string;
  isNew?: boolean;
  /** FAO food group label from ingredient master (display). */
  faoFoodGroupName?: string;
  /** Comma-separated allergen names from ingredient master (display). */
  allergenNames?: string;
  allergenIds?: number[];
  intoleranceNames?: string;
  intoleranceIds?: number[];
  allergenFreeIds?: number[];
  intoleranceFreeIds?: number[];
  /** True after master GetById taxonomy was applied (even if empty). */
  taxonomyHydrated?: boolean;
}

export interface FpBulkServingMeasurement {
  qty?: number | null;
  servingSize?: string | null;
  servingMeasure?: string | null;
  amount?: number | null;
  unit?: string | null;
  calories?: number | null;
  carbs?: number | null;
  protein?: number | null;
  fat?: number | null;
  fiber?: number | null;
  uomId?: string | null;
  categoryId?: string | null;
  subcategoryId?: string | null;
  categoryName?: string | null;
  subcategoryName?: string | null;
}

export interface FpBulkNutrientItem {
  nutrientId: string;
  nutrientName: string;
  unitId: string;
  unitName: string;
  amount: string | number;
  percentDV?: string | number;
  rda?: string | number;
}

export interface FpBulkClaimDraft {
  claimTypeId: string;
  claimTypeName: string;
  claimText: string;
  selectedTagIds: string[];
  selectedTagNames: string[];
  /** Stable ng-select items for this row (avoids re-filter wiping selections). */
  availableTags?: FpBulkLookupOption[];
  isDone?: boolean;
}

export interface FpBulkHealthConditionGroup {
  groupId: string;
  groupName: string;
  tags: Array<{ id: string; name: string }>;
}

export interface FpBulkNutrientTagOption extends FpBulkLookupOption {
  groupId?: string | number | null;
}

export interface FpBulkClaimTagOption extends FpBulkLookupOption {
  claimTypeId?: string | number | null;
  productBenefitId?: string | number | null;
}

export interface FpBulkAssignment {
  mode: 'maker-and-checker' | 'checker-only';
  makerEmail?: string | null;
  checkerEmail?: string | null;
  dueDate?: string | null;
  priority?: string;
  notes?: string | null;
}

export interface FpBulkImportItem {
  payload: Record<string, unknown>;
  assignment?: FpBulkAssignment | null;
}

export interface FpBulkRowSelections {
  status: string;
  // Company location
  countryId?: string | null;
  stateId?: string | null;
  cityId?: string | null;
  pincodeId?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  cityName?: string | null;
  pincode?: string | null;
  // Product masters
  healthColorCodeId?: string | null;
  dietaryPreferenceId?: string | number | null;
  productType?: string | null;
  productForm?: string | null;
  dataSourceId?: string | number | null;
  databaseSourceId?: string | number | null;
  countryOfOriginId?: string | number | null;
  shelfLifeUnit?: string | null;
  netQuantityUnit?: string | null;
  // Images
  frontImage?: string | null;
  backImage?: string | null;
  leftImage?: string | null;
  rightImage?: string | null;
  frontImageUploading?: boolean;
  authorizedCertificateImage?: string | null;
  authorizedCertificateUploading?: boolean;
  authorizedLicenseImage?: string | null;
  authorizedLicenseUploading?: boolean;
  // Entities
  manufacturedBy: FpBulkEntitySelections;
  marketedBy: FpBulkEntitySelections;
  packagedBy: FpBulkEntitySelections;
  importedBy: FpBulkEntitySelections;
  exportedBy: FpBulkEntitySelections;
  packagedBySection4: FpBulkEntitySelections;
  // Categories
  fssaiCategoryId?: string | null;
  fssaiSubCategoryId?: string | null;
  fssaiSubSubCategoryId?: string | null;
  eStoreCategoryId?: string | null;
  eStoreSubCategoryId?: string | null;
  eStoreSubSubCategoryId?: string | null;
  fssaiCategoryName?: string | null;
  fssaiSubCategoryName?: string | null;
  fssaiSubSubCategoryName?: string | null;
  eStoreCategoryName?: string | null;
  eStoreSubCategoryName?: string | null;
  eStoreSubSubCategoryName?: string | null;
  // Sensory
  tasteIds: Array<string | number>;
  textureIds: Array<string | number>;
  flavourIds: Array<string | number>;
  aromaIds: Array<string | number>;
  // Geo
  continentId?: string | number | null;
  regionId?: string | number | null;
  subRegionId?: string | number | null;
  cuisineIds: Array<string | number>;
  occasionIds: Array<string | number>;
  locationIds: Array<string | number>;
  seasonIds: Array<string | number>;
  continentName?: string | null;
  regionName?: string | null;
  subRegionName?: string | null;
  // Ingredients / UOM / nutrients
  ingredientDrafts: FpBulkIngredientDraft[];
  ingredients: FpBulkResolvedIngredient[];
  servingMeasurements: FpBulkServingMeasurement[];
  macronutrients: FpBulkNutrientItem[];
  micronutrients: FpBulkNutrientItem[];
  // Allergens
  allergenDeclaredIds: Array<string | number>;
  mayContainIds: Array<string | number>;
  producedInFacilityIds: Array<string | number>;
  allergenContainsIds: Array<string | number>;
  allergenFreeIds: Array<string | number>;
  intoleranceContainsIds: Array<string | number>;
  intoleranceFreeIds: Array<string | number>;
  // Claims
  productClaims: FpBulkClaimDraft[];
  productBenefitClaims: FpBulkClaimDraft[];
  ingredientClaims: FpBulkClaimDraft[];
  healthConditionTags: FpBulkHealthConditionGroup[];
  generatedFssaiNutrientTags: Array<{ fssaiTagName: string; fssaiNutrientTagMasterId?: number }>;
  // SKU
  skuDetails: FpBulkSkuDraft[];
  // Rework
  reworkSuggestionId?: string | number | null;
  reworkSuggestionName?: string | null;
  certificationRating?: number | null;
  assignment?: FpBulkAssignment | null;
}

export interface FpBulkParsedText {
  companyName: string;
  address: string;
  executiveOwner: string;
  mobileNumber: string;
  email: string;
  gstStatus: string;
  brandName: string;
  productName: string;
  brandWebsite: string;
  instagram: string;
  facebook: string;
  linkedin: string;
  shelfLife: string;
  netQuantity: string;
  lotNumber: string;
  referenceUrl: string;
  dataCapturingDate: string;
  dateOfManufacturing: string;
  dateOfExpiry: string;
  bestBeforeDate: string;
  manufacturedByName: string;
  manufacturedByAddress: string;
  manufacturedByLicenseNo: string;
  marketedByName: string;
  marketedByAddress: string;
  marketedByLicenseNo: string;
  packagedByName: string;
  packagedByAddress: string;
  packagedByLicenseNo: string;
  importedByName: string;
  importedByAddress: string;
  importedByLicenseNo: string;
  exportedByName: string;
  exportedByAddress: string;
  exportedByLicenseNo: string;
  packagedBySection4Name: string;
  packagedBySection4Address: string;
  packagedBySection4LicenseNo: string;
  /** CSV name hints — resolved into selections when lookups load */
  country: string;
  state: string;
  city: string;
  pincode: string;
  healthColorCode: string;
  dietaryPreference: string;
  productType: string;
  productForm: string;
  dataSource: string;
  databaseSource: string;
  countryOfOrigin: string;
  shelfLifeUnit: string;
  netQuantityUnit: string;
  fssaiCategory: string;
  fssaiSubCategory: string;
  fssaiSubSubCategory: string;
  eStoreCategory: string;
  eStoreSubCategory: string;
  eStoreSubSubCategory: string;
  tastes: string;
  textures: string;
  flavours: string;
  aromas: string;
  continent: string;
  region: string;
  subRegion: string;
  cuisines: string;
  occasions: string;
  geoLocations: string;
  seasons: string;
  manufacturedByCountry: string;
  manufacturedByState: string;
  manufacturedByCity: string;
  manufacturedByPincode: string;
  marketedByCountry: string;
  marketedByState: string;
  marketedByCity: string;
  marketedByPincode: string;
  packagedByCountry: string;
  packagedByState: string;
  packagedByCity: string;
  packagedByPincode: string;
  importedByCountry: string;
  importedByState: string;
  importedByCity: string;
  importedByPincode: string;
  exportedByCountry: string;
  exportedByState: string;
  exportedByCity: string;
  exportedByPincode: string;
  packagedBySection4Country: string;
  packagedBySection4State: string;
  packagedBySection4City: string;
  packagedBySection4Pincode: string;
  skuRaw: string;
  ingredientsRaw: string;
  macrosRaw: string;
  microsRaw: string;
  servingMeasurementsRaw: string;
  allergenDeclared: string;
  mayContain: string;
  producedInFacility: string;
  allergenDeclaredNotes: string;
  mayContainNotes: string;
  producedInFacilityNotes: string;
  allergenContains: string;
  allergenFree: string;
  intoleranceContains: string;
  intoleranceFree: string;
  productClaimsRaw: string;
  productBenefitsRaw: string;
  ingredientClaimsRaw: string;
  healthConditionTagsRaw: string;
  reworkSuggestion: string;
  certificationRating: string;
  status: string;
  directionForUse: string;
  warnings: string;
  disclaimer: string;
  reworkDescription: string;
}

export interface FpBulkPreviewRow {
  rowNumber: number;
  valid: boolean;
  configured: boolean;
  error?: string;
  cells: Record<string, string>;
  display: Record<string, string>;
  parsed: FpBulkParsedText;
  selections: FpBulkRowSelections;
  payload?: Record<string, unknown>;
}

export interface FpBulkParseResult {
  rows: FpBulkPreviewRow[];
  validCount: number;
  errorCount: number;
  totalCount: number;
  fileError?: string;
}

export interface FpBulkLookups {
  healthColorCodes: FpBulkHealthColorOption[];
  dietaryPreferences: FpBulkLookupOption[];
  dataSources: FpBulkLookupOption[];
  databaseSources: FpBulkLookupOption[];
  countries: FpBulkLookupOption[];
  tastes: FpBulkLookupOption[];
  textures: FpBulkLookupOption[];
  flavours: FpBulkLookupOption[];
  aromas: FpBulkLookupOption[];
  continents: FpBulkLookupOption[];
  occasions: FpBulkLookupOption[];
  locations: FpBulkLookupOption[];
  seasons: FpBulkLookupOption[];
  cuisines: FpBulkLookupOption[];
  allergens: FpBulkLookupOption[];
  allergenFree: FpBulkLookupOption[];
  intolerances: FpBulkLookupOption[];
  intoleranceFree: FpBulkLookupOption[];
  fssaiCategories: FpBulkLookupOption[];
  eStoreCategories: FpBulkLookupOption[];
  units: FpBulkLookupOption[];
  reworkSuggestions: FpBulkLookupOption[];
  nutrients: FpBulkLookupOption[];
  nutrientGroups: FpBulkLookupOption[];
  nutrientTags: FpBulkNutrientTagOption[];
  claimTypes: FpBulkLookupOption[];
  claimTags: FpBulkClaimTagOption[];
  productBenefits: FpBulkLookupOption[];
  productBenefitTags: FpBulkClaimTagOption[];
  /** Product type names from Product Type master (fallback: FP_BULK_PRODUCT_TYPES). */
  productTypes: string[];
}

export const FP_BULK_MAX_ROWS = 5000; // same cap as recipe/supplement bulk-create

export const FP_BULK_STATUS_OPTIONS = [
  'Draft',
  'To be assigned',
  'Assigned to Maker',
  'Save as Draft',
  'First review',
  'Assigned to Checker',
  'Corrections Needed',
  'Corrections Done',
  'Checked',
  'Published',
];

export const FP_BULK_PRODUCT_TYPES = ['RTC', 'RTE', 'RTD', 'Supplement'];
export const FP_BULK_PRODUCT_FORMS = ['Solid', 'Liquid'];
export const FP_BULK_SHELF_LIFE_UNITS = ['Days', 'Months', 'Years', 'Hours', 'Minutes'];
export const FP_BULK_NET_QTY_UNITS = ['g', 'kg', 'ml', 'L', 'mg', 'mcg'];

export const FP_BULK_TEMPLATE_HEADERS = [
  // 1 Company
  'CompanyName',
  'Address',
  'ExecutiveOwner',
  'MobileNumber',
  'Email',
  'GstStatus',
  'Country',
  'CountryId',
  'State',
  'StateId',
  'City',
  'CityId',
  'Pincode',
  'PincodeId',
  // 2 Product
  'BrandName',
  'ProductName',
  'HealthColorCode',
  'HealthColorCodeId',
  'DietaryPreference',
  'DietaryPreferenceId',
  'ProductType',
  'ProductForm',
  'DataSource',
  'DataSourceId',
  'DatabaseSource',
  'DatabaseSourceId',
  'CountryOfOrigin',
  'CountryOfOriginId',
  'BrandWebsite',
  'Instagram',
  'Facebook',
  'LinkedIn',
  'ShelfLife',
  'ShelfLifeUnit',
  'NetQuantity',
  'NetQuantityUnit',
  'LotNumber',
  'ReferenceUrl',
  'DataCapturingDate',
  'DateOfManufacturing',
  'DateOfExpiry',
  'BestBeforeDate',
  // 3 Entities
  'ManufacturedByName',
  'ManufacturedByAddress',
  'ManufacturedByLicenseNo',
  'ManufacturedByCountry',
  'ManufacturedByCountryId',
  'ManufacturedByState',
  'ManufacturedByStateId',
  'ManufacturedByCity',
  'ManufacturedByCityId',
  'ManufacturedByPincode',
  'ManufacturedByPincodeId',
  'MarketedByName',
  'MarketedByAddress',
  'MarketedByLicenseNo',
  'MarketedByCountry',
  'MarketedByCountryId',
  'MarketedByState',
  'MarketedByStateId',
  'MarketedByCity',
  'MarketedByCityId',
  'MarketedByPincode',
  'MarketedByPincodeId',
  'PackagedByName',
  'PackagedByAddress',
  'PackagedByLicenseNo',
  'PackagedByCountry',
  'PackagedByCountryId',
  'PackagedByState',
  'PackagedByStateId',
  'PackagedByCity',
  'PackagedByCityId',
  'PackagedByPincode',
  'PackagedByPincodeId',
  // 4 Import / Export
  'ImportedByName',
  'ImportedByAddress',
  'ImportedByLicenseNo',
  'ImportedByCountry',
  'ImportedByCountryId',
  'ImportedByState',
  'ImportedByStateId',
  'ImportedByCity',
  'ImportedByCityId',
  'ImportedByPincode',
  'ImportedByPincodeId',
  'ExportedByName',
  'ExportedByAddress',
  'ExportedByLicenseNo',
  'ExportedByCountry',
  'ExportedByCountryId',
  'ExportedByState',
  'ExportedByStateId',
  'ExportedByCity',
  'ExportedByCityId',
  'ExportedByPincode',
  'ExportedByPincodeId',
  'PackagedBySection4Name',
  'PackagedBySection4Address',
  'PackagedBySection4LicenseNo',
  'PackagedBySection4Country',
  'PackagedBySection4CountryId',
  'PackagedBySection4State',
  'PackagedBySection4StateId',
  'PackagedBySection4City',
  'PackagedBySection4CityId',
  'PackagedBySection4Pincode',
  'PackagedBySection4PincodeId',
  // 5 SKU  (pipe: Name|NetWeight|MRP|ManufacturedBy|MarketedBy|PackagedBy|Unit|Country|Barcode)
  'SKUDetails',
  // 6 Categories / Sensory / Geo
  'FssaiCategory',
  'FssaiCategoryId',
  'FssaiSubCategory',
  'FssaiSubCategoryId',
  'FssaiSubSubCategory',
  'FssaiSubSubCategoryId',
  'EStoreCategory',
  'EStoreCategoryId',
  'EStoreSubCategory',
  'EStoreSubCategoryId',
  'EStoreSubSubCategory',
  'EStoreSubSubCategoryId',
  'Tastes',
  'TasteIds',
  'Textures',
  'TextureIds',
  'Flavours',
  'FlavourIds',
  'Aromas',
  'AromaIds',
  'Continent',
  'ContinentId',
  'Region',
  'RegionId',
  'SubRegion',
  'SubRegionId',
  'Cuisines',
  'CuisineIds',
  'Occasions',
  'OccasionIds',
  'GeoLocations',
  'GeoLocationIds',
  'Seasons',
  'SeasonIds',
  // 7 Allergens & Intolerance
  'AllergenDeclared',
  'AllergenDeclaredIds',
  'MayContain',
  'MayContainIds',
  'ProducedInFacility',
  'ProducedInFacilityIds',
  'AllergenDeclaredNotes',
  'MayContainNotes',
  'ProducedInFacilityNotes',
  'AllergenContains',
  'AllergenContainsIds',
  'AllergenFree',
  'AllergenFreeIds',
  'IntoleranceContains',
  'IntoleranceContainsIds',
  'IntoleranceFree',
  'IntoleranceFreeIds',
  // 8 UOM  (pipe: Label|Qty|Unit|Calories|Carbs|Protein|Fat|Fiber;;...)
  'ServingMeasurements',
  // 9 Ingredients  (Name|Amount|Unit;;...)
  'Ingredients',
  // 10 Nutrients  (Name|Unit|Amount|%DV|RDA;;...)
  'Macros',
  'Micronutrients',
  // 11 Claims & tags  (Type|Text;;...)  / benefits same / health tags Name or Id
  'ProductClaims',
  'ProductBenefits',
  'HealthConditionTags',
  // 12 Ingredient claims
  'IngredientClaims',
  // 13 Rework
  'ReworkSuggestion',
  'ReworkSuggestionId',
  'ReworkDescription',
  // 14 Directions+
  'DirectionForUse',
  'Warnings',
  'Disclaimer',
  'CertificationRating',
  // 15 Status
  'Status',
];

/** Example data row so downloaded template shows how to fill PackagedBy / masters. */
export const FP_BULK_TEMPLATE_SAMPLE_ROW: Record<string, string> = {
  CompanyName: 'ABC Foods Pvt Ltd',
  Address: 'Plot 12, Sector 62, Noida',
  ExecutiveOwner: 'Rahul Sharma',
  MobileNumber: '9876543210',
  Email: 'info@abcfoods.com',
  GstStatus: 'Registered',
  Country: 'India',
  CountryId: '',
  State: 'Uttar Pradesh',
  StateId: '',
  City: 'Gautam Buddha Nagar',
  CityId: '',
  Pincode: '201301',
  PincodeId: '',
  BrandName: 'NutriBite',
  ProductName: 'Multigrain Cookies',
  HealthColorCode: 'Green Color Code',
  HealthColorCodeId: '',
  DietaryPreference: 'Veg',
  DietaryPreferenceId: '',
  ProductType: 'RTE',
  ProductForm: 'Solid',
  DataSource: 'FSSAI',
  DataSourceId: '',
  DatabaseSource: 'Manufacturer Label',
  DatabaseSourceId: '',
  CountryOfOrigin: 'India',
  CountryOfOriginId: '',
  BrandWebsite: 'https://www.abcfoods.com',
  Instagram: '@abcfoods',
  Facebook: 'ABC Foods',
  LinkedIn: 'ABC Foods',
  ShelfLife: '12',
  ShelfLifeUnit: 'Months',
  NetQuantity: '250',
  NetQuantityUnit: 'g',
  LotNumber: 'LOT-2024-001',
  ReferenceUrl: '',
  DataCapturingDate: '',
  DateOfManufacturing: '',
  DateOfExpiry: '',
  BestBeforeDate: '',
  ManufacturedByName: 'ABC Foods Manufacturing',
  ManufacturedByAddress: 'Industrial Area Phase 1',
  ManufacturedByLicenseNo: 'LIC-MFG-001',
  ManufacturedByCountry: 'India',
  ManufacturedByCountryId: '',
  ManufacturedByState: 'Uttar Pradesh',
  ManufacturedByStateId: '',
  ManufacturedByCity: 'Gautam Buddha Nagar',
  ManufacturedByCityId: '',
  ManufacturedByPincode: '201301',
  ManufacturedByPincodeId: '',
  MarketedByName: 'ABC Foods Marketing',
  MarketedByAddress: 'Sector 18',
  MarketedByLicenseNo: 'LIC-MKT-001',
  MarketedByCountry: 'India',
  MarketedByCountryId: '',
  MarketedByState: 'Uttar Pradesh',
  MarketedByStateId: '',
  MarketedByCity: 'Gautam Buddha Nagar',
  MarketedByCityId: '',
  MarketedByPincode: '201301',
  MarketedByPincodeId: '',
  PackagedByName: 'ABC Packaging Unit',
  PackagedByAddress: 'Plot 5, Packaging Zone',
  PackagedByLicenseNo: 'LIC-PKG-001',
  PackagedByCountry: 'India',
  PackagedByCountryId: '',
  PackagedByState: 'Uttar Pradesh',
  PackagedByStateId: '',
  PackagedByCity: 'Gautam Buddha Nagar',
  PackagedByCityId: '',
  PackagedByPincode: '201301',
  PackagedByPincodeId: '',
  ImportedByName: 'Global Imports Pvt Ltd',
  ImportedByAddress: 'Baroda House, Central Delhi',
  ImportedByLicenseNo: 'LIC-IMP-001',
  ImportedByCountry: 'India',
  ImportedByCountryId: '',
  ImportedByState: 'Delhi',
  ImportedByStateId: '',
  ImportedByCity: 'Central Delhi',
  ImportedByCityId: '',
  ImportedByPincode: '110001',
  ImportedByPincodeId: '',
  ExportedByName: 'ABC Export Wing',
  ExportedByAddress: 'Export Zone, Noida',
  ExportedByLicenseNo: 'LIC-EXP-001',
  ExportedByCountry: 'India',
  ExportedByCountryId: '',
  ExportedByState: 'Uttar Pradesh',
  ExportedByStateId: '',
  ExportedByCity: 'Gautam Buddha Nagar',
  ExportedByCityId: '',
  ExportedByPincode: '201301',
  ExportedByPincodeId: '',
  PackagedBySection4Name: 'ABC Packaging Unit',
  PackagedBySection4Address: 'Plot 5, Packaging Zone',
  PackagedBySection4LicenseNo: 'LIC-PKG-001',
  PackagedBySection4Country: 'India',
  PackagedBySection4CountryId: '',
  PackagedBySection4State: 'Uttar Pradesh',
  PackagedBySection4StateId: '',
  PackagedBySection4City: 'Gautam Buddha Nagar',
  PackagedBySection4CityId: '',
  PackagedBySection4Pincode: '201301',
  PackagedBySection4PincodeId: '',
  SKUDetails: '250g Pack|250|199|ABC Mfg|ABC Mkt|ABC Pkg|g|India|8901234567890',
  FssaiCategory: 'FSSAI One',
  FssaiCategoryId: '',
  FssaiSubCategory: '',
  FssaiSubCategoryId: '',
  FssaiSubSubCategory: '',
  FssaiSubSubCategoryId: '',
  EStoreCategory: 'zomato',
  EStoreCategoryId: '',
  EStoreSubCategory: '',
  EStoreSubCategoryId: '',
  EStoreSubSubCategory: '',
  EStoreSubSubCategoryId: '',
  Tastes: 'Sweet,Spicy',
  TasteIds: '',
  Textures: 'Tender',
  TextureIds: '',
  Flavours: 'Sweet,Savoury',
  FlavourIds: '',
  Aromas: 'Aromatic Spices',
  AromaIds: '',
  Continent: 'Asia',
  ContinentId: '',
  Region: '',
  RegionId: '',
  SubRegion: '',
  SubRegionId: '',
  Cuisines: 'Bengali',
  CuisineIds: '',
  Occasions: 'Diwali',
  OccasionIds: '',
  GeoLocations: 'India',
  GeoLocationIds: '',
  Seasons: 'All Seasons',
  SeasonIds: '',
  AllergenDeclared: 'Wheat,Dairy',
  AllergenDeclaredIds: '',
  MayContain: 'Almond,Cashew Nut',
  MayContainIds: '',
  ProducedInFacility: 'Soya',
  ProducedInFacilityIds: '',
  AllergenDeclaredNotes: 'Contains gluten and milk',
  MayContainNotes: 'May contain tree nuts',
  ProducedInFacilityNotes: 'Shared facility with soy',
  AllergenContains: 'Wheat',
  AllergenContainsIds: '',
  AllergenFree: '',
  AllergenFreeIds: '',
  IntoleranceContains: 'Lactose',
  IntoleranceContainsIds: '',
  IntoleranceFree: '',
  IntoleranceFreeIds: '',
  ServingMeasurements: 'Per 100g|100|g|450|55|8|18|3',
  Ingredients: 'Wheat Flour|100|g;;Sugar|20|g',
  Macros: 'Energy / Calories|KCAL|250||;;Protein|g|8||;;Total Fat|g|10||;;Dietary Fibre|g|3||',
  Micronutrients: 'Iron|mg|2.5||;;Calcium|mg|80||',
  ProductClaims: 'Health Claim|Rich in fibre',
  ProductBenefits: 'Benefit|Supports digestion',
  HealthConditionTags: '',
  IngredientClaims: 'Claim|Contains whole grains',
  ReworkSuggestion: '',
  ReworkSuggestionId: '',
  ReworkDescription: '',
  DirectionForUse: 'Open and consume',
  Warnings: 'Not suitable for infants',
  Disclaimer: 'For bulk upload testing',
  CertificationRating: '4',
  Status: 'Draft',
};

export function createEmptyEntitySelections(): FpBulkEntitySelections {
  return {
    countryId: null,
    stateId: null,
    cityId: null,
    pincodeId: null,
    countryName: null,
    stateName: null,
    cityName: null,
    pincode: null,
    licenseImage: null,
  };
}

export interface FpBulkFieldError {
  field: string;
  message: string;
  section: number;
}

export function createDefaultFpBulkSelections(): FpBulkRowSelections {
  return {
    status: 'Draft',
    countryId: null,
    stateId: null,
    cityId: null,
    pincodeId: null,
    healthColorCodeId: null,
    dietaryPreferenceId: null,
    productType: null,
    productForm: null,
    dataSourceId: null,
    databaseSourceId: null,
    countryOfOriginId: null,
    shelfLifeUnit: null,
    netQuantityUnit: null,
    frontImage: null,
    backImage: null,
    leftImage: null,
    rightImage: null,
    frontImageUploading: false,
    authorizedCertificateImage: null,
    authorizedCertificateUploading: false,
    authorizedLicenseImage: null,
    authorizedLicenseUploading: false,
    manufacturedBy: createEmptyEntitySelections(),
    marketedBy: createEmptyEntitySelections(),
    packagedBy: createEmptyEntitySelections(),
    importedBy: createEmptyEntitySelections(),
    exportedBy: createEmptyEntitySelections(),
    packagedBySection4: createEmptyEntitySelections(),
    tasteIds: [],
    textureIds: [],
    flavourIds: [],
    aromaIds: [],
    cuisineIds: [],
    occasionIds: [],
    locationIds: [],
    seasonIds: [],
    ingredientDrafts: [],
    ingredients: [],
    servingMeasurements: [],
    macronutrients: [],
    micronutrients: [],
    allergenDeclaredIds: [],
    mayContainIds: [],
    producedInFacilityIds: [],
    allergenContainsIds: [],
    allergenFreeIds: [],
    intoleranceContainsIds: [],
    intoleranceFreeIds: [],
    productClaims: [],
    productBenefitClaims: [],
    ingredientClaims: [],
    healthConditionTags: [],
    generatedFssaiNutrientTags: [],
    skuDetails: [],
    certificationRating: null,
    assignment: null,
  };
}
